import type * as THREE from 'three';
import {isSketchLineObject, type SketchLineObject, type SketchPointObject} from '../../mesh/sketch';

//------ 强力剪裁默认中点端点迁移 开始------
/**
 * 默认中点端点迁移输入。
 *
 * 说明：
 * 1. `line` 是拥有默认中点的宿主线；
 * 2. `position` 是裁剪边界位置，用来确认本次迁移确实落在宿主线默认中点；
 * 3. `replacementPoint` 是用于承接真实业务拓扑的新端点或既有端点。
 */
export interface SketchTrimOwnedMidPointTransferInput {
    /** 拥有默认中点的宿主线。 */
    line: SketchLineObject;
    /** 裁剪边界位置。 */
    position: THREE.Vector3;
    /** 承接外部线端点的真实控制点。 */
    replacementPoint: SketchPointObject;
    /** 点位匹配容差。 */
    positionTolerance: number;
}

/**
 * 强力剪裁线端点迁移服务。
 *
 * 设计说明：
 * 1. `SketchLineObject.getMidPoint()` 返回的是派生辅助点，会在宿主线刷新时自动重算；
 * 2. 当其它线通过点捕捉把该默认中点当作端点时，剪裁宿主线不能继续复用该派生点；
 * 3. 本服务负责把外部线端点迁移到真实控制点，避免派生中点重算时拖动外部线。
 */
export class SketchTrimLineEndpointTransferService {
    /**
     * 判断候选点是否为指定线段自己的默认中点。
     *
     * @param line 宿主线。
     * @param point 候选点。
     * @returns 若候选点是宿主线默认中点则返回 true。
     */
    public isOwnedDefaultMidPoint(line: SketchLineObject, point: SketchPointObject): boolean {
        if (typeof line.getMidPoint !== 'function') {
            return false;
        }
        return line.getMidPoint() === point;
    }

    /**
     * 将宿主线默认中点上的外部线端点迁移到真实控制点。
     *
     * @param input 迁移输入。
     * @returns 已迁移端点的外部线列表。
     */
    public transferOwnedDefaultMidPointExternalLineEndpoints(
        input: SketchTrimOwnedMidPointTransferInput,
    ): SketchLineObject[] {
        if (typeof input.line.getMidPoint !== 'function') {
            return [];
        }

        const ownedDefaultMidPoint = input.line.getMidPoint();
        if (ownedDefaultMidPoint === input.replacementPoint) {
            return [];
        }

        const distanceSquared = ownedDefaultMidPoint.getPosition().distanceToSquared(input.position);
        const toleranceSquared = input.positionTolerance ** 2;
        if (!Number.isFinite(distanceSquared) || distanceSquared > toleranceSquared) {
            return [];
        }

        return this.transferExternalLineEndpoints(
            ownedDefaultMidPoint,
            input.replacementPoint,
            input.line,
        );
    }

    /**
     * 迁移指定源点上的外部线端点。
     *
     * @param sourcePoint 需要解除外部端点引用的源点。
     * @param replacementPoint 承接外部端点的新点。
     * @param ownerLine 源点所属宿主线。
     * @returns 已迁移端点的外部线列表。
     */
    private transferExternalLineEndpoints(
        sourcePoint: SketchPointObject,
        replacementPoint: SketchPointObject,
        ownerLine: SketchLineObject,
    ): SketchLineObject[] {
        const transferredLines: SketchLineObject[] = [];
        const connectedEdges = sourcePoint.getConnectedEdgesSnapshot();

        connectedEdges.forEach((edge) => {
            if (edge === ownerLine || !isSketchLineObject(edge)) {
                return;
            }

            let hasTransferred = false;
            if (edge.startPoint === sourcePoint) {
                edge.setStartPoint(replacementPoint);
                hasTransferred = true;
            }
            if (edge.endPoint === sourcePoint) {
                edge.setEndPoint(replacementPoint);
                hasTransferred = true;
            }

            if (hasTransferred) {
                transferredLines.push(edge);
            }
        });

        return transferredLines;
    }
}
//------ 强力剪裁默认中点端点迁移 结束------
